import { useEffect, useMemo, useState } from "react";
import apiClient from "../../../business/network/ApiClient";
import ScheduledItem from "./ScheduledItem";
import Tabs from "../Shared/Tabs";
import Empty from "./Empty";
import { GrtAnalyticsGA4 } from "../../specialized/GrtAnalyticsGA4";
import ListButton from "../Shared/ListButton";
import clsx from "clsx";
import { LoaderCircle } from "lucide-react";
import { selectInstance } from "../../../business/helper/Singleton";
import DeleteModal from "../../specialized/DeleteModal";
import ScheduledEmailSettingsModal from "./ScheduledEmailSettingsModal";
import { emailSettingsFor, ownerEmailOf } from "./EmailDeliveryOptions";
import { CurrentUser } from "../../../business/helper/CurrentUser";
import { FEATURE_FLAGS, useFeatureFlag } from "../../context/FeatureFlagsContext";
import { toast } from "react-toastify";
import { parseToHistoryResponse, scheduledFeedMeta } from "../../../business/helper/ScheduledFeed";

const tabs = ["All", "Unread"];

// status: "loading" until the first list has arrived, "error" when the request failed, else "ready".
// The list is only ever empty for a reason, and "No Tasks Yet" is just one of them.
const Scheduled = ({ data: incomingData, status = "ready", onRead, threadId, onRefreshData }) => {
  const [active, setActive] = useState("All");
  const [allData, setAllData] = useState(incomingData);
  const [displayData, setDisplayData] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [listCoefficient, setListCoefficient] = useState(10);
  const [deleteModalInfo, setDeleteModalInfo] = useState(null);
  // The task whose "Send to email" screen is open
  const [emailModalItem, setEmailModalItem] = useState(null);
  // The server already leaves emailDelivery null while the scheduled_email feature is off; this
  // keeps the menu's email entries out even when a cached list predates the switch.
  const emailFeatureOn = useFeatureFlag(FEATURE_FLAGS.SCHEDULED_EMAIL);
  const ownerEmail = ownerEmailOf(CurrentUser.GetCurrentUserData());
  const [selected, setSelected] = useState(selectInstance.state);
  const [unread, setUnread] = useState(0);

  const [instantReaded, setInstantReaded] = useState([]);

  const handleAnalytics = (ga) => {
    if (ga) {
      setAnalytics(ga);
    }
  };

  const handleDelete = (id, title) => {
    console.log(`id: ${id} ; title: ${title}`);
    setDeleteModalInfo({
      id,
      title,
    });
  };

  const handleCancel = () => {
    setDeleteModalInfo(null);
  };

  const confirmDelete = async () => {
    if (!deleteModalInfo) return;

    const { id } = deleteModalInfo;
    setDeleteModalInfo(null);

    // Remove item from list immediately for real-time UI update
    setAllData((prevData) =>
      prevData.filter((item) => item.scheduledQuestion.id !== id)
    );

    try {
      await apiClient.deleteScheduledQuestion(id);
      onRefreshData();
    } catch (error) {
      console.error("Failed to delete task", error);
      onRefreshData();
    }

    analytics?.trackEventAnalytics(
      "MessageNotification",
      "DeleteThread",
      "click"
    );
  };

  // The three-dots switch: repaint at once, send behind it, roll back if the save fails.
  const handleToggleEmail = async (item, next) => {
    const id = item.scheduledQuestion.id;
    const previous = item.emailDelivery;
    const settings = emailSettingsFor(next, ownerEmail, previous?.extraRecipients ?? []);

    const apply = (emailDelivery) =>
      setAllData((prevData) =>
        prevData.map((row) =>
          row.scheduledQuestion.id === id ? { ...row, emailDelivery } : row
        )
      );

    apply({ ...(previous ?? {}), ...settings, configured: true });
    try {
      const saved = await apiClient.updateScheduledEmailSettings(id, settings);
      apply(saved);
      toast.success(
        saved.sendToMe
          ? `Answers will be emailed to ${saved.ownerEmail}.`
          : "Answers will no longer be emailed to you."
      );
    } catch (error) {
      apply(previous);
      toast.error(
        (typeof error?.response?.data === "string" && error.response.data) ||
          "The email setting could not be saved."
      );
    }
    analytics?.trackEventAnalytics("MessageNotification", "ToggleEmail", next ? "on" : "off");
  };

  const handleSaveEmailSettings = async (item, settings) => {
    const id = item.scheduledQuestion.id;
    const saved = await apiClient.updateScheduledEmailSettings(id, settings);
    setAllData((prevData) =>
      prevData.map((row) =>
        row.scheduledQuestion.id === id ? { ...row, emailDelivery: saved } : row
      )
    );
    toast.success("Email delivery updated.");
    analytics?.trackEventAnalytics("MessageNotification", "EmailSettings", "save");
  };

  const handleRead = async (item) => {
    try {
      if (item) {
        await apiClient.markScheduledQuestionAsRead(item.scheduledQuestion.id);
        // The list holds the latest page of answers; the chat pages further back itself
        // (useScheduledFeedPaging) from what this tells it.
        const entries = parseToHistoryResponse(item);
        entries.scheduled = scheduledFeedMeta(item, entries);
        onRead(entries, item.scheduledQuestion.threadId);
      }
    } catch (error) {
      console.error("Failed to read task", error);
    }
  };

  useEffect(() => {
    if (incomingData) {
      console.log("Setting data of Scheduled task from MessageNotification component");

      let idsToRemove = [];

      instantReaded.forEach((id) => {
        const item = incomingData.find((item) => item.scheduledQuestion.id === id);
        if (item) {
          if (item.scheduledQuestion.readed === true) {
            idsToRemove.push(id);
          }
          else {
            item.scheduledQuestion.readed = true;
          }
        }
      });

      const result = instantReaded.filter(id => !idsToRemove.includes(id));
      setInstantReaded(result);

      setUnread(incomingData.filter(
        (item) => !item.scheduledQuestion.readed
      ).length);

      let dataSort = incomingData.sort((a, b) => b.scheduledQuestion.id - a.scheduledQuestion.id);
      setAllData(dataSort);
    }
  }, [incomingData]);

  //This useEffect is used when we are changing the tabs.
  useEffect(() => {
    if (active !== undefined) {
      let dataFilter = allData.filter((item) =>
        active === "All" ? item : !item.scheduledQuestion.readed
      )
      let dataSort = dataFilter.sort((a, b) => b.scheduledQuestion.id - a.scheduledQuestion.id);
      setDisplayData(dataSort);
    }
  }, [active]);

  useEffect(() => {
    if (allData) {
      let dataFilter = allData.filter((item) =>
        active === "All" ? item : !item.scheduledQuestion.readed
      )
      let dataSort = dataFilter.sort((a, b) => b.scheduledQuestion.id - a.scheduledQuestion.id);
      setDisplayData(dataSort);
    }
  }, [allData]);

  useEffect(() => {
    const event = () => setSelected(selectInstance.state);
    selectInstance.subscribe(event);

    return () => {
      selectInstance.unsubscribe(event);
    };
  }, []);

  return (
    <div className="flex flex-col gap-3 h-full max-h-full p-3">
      <h2 className="font-semibold text-xl text-dark-blue-100">
        Scheduled Tasks{" "}
        {(status !== "loading" || !!allData?.length) && (
          <span className="text-base text-dimgray-200">
            ({allData ? allData.length : 0})
          </span>
        )}
      </h2>

      <Tabs
        unread={unread}
        items={tabs}
        active={active}
        setActive={(id) => setActive(id)}
      />
      <div className="flex flex-col space-y-3 overflow-y-auto" key={displayData ? `scheduledgrid_${displayData.length}` : `scheduledgrid_0`}>
        {displayData && !!displayData.length ? (
          <>
            {displayData.map(
              (item, index) =>
              (
                <ScheduledItem
                  key={item.scheduledQuestion.threadId}
                  className={clsx({
                    "!h-0 overflow-hidden !m-0 !py-0":
                      index + 1 > listCoefficient,
                    "bg-whitesmoke-400 !border-l-4 !border-l-[#2A354A]":
                      selected === item.scheduledQuestion.jobId &&
                      item.scheduledQuestion.threadId === threadId,
                  })}
                  frequencyId={item.scheduledQuestion.frequencyId}
                  granularityId={item.scheduledQuestion.granularityId}
                  granularityValue={item.scheduledQuestion.granularityValue}
                  description={item.scheduledQuestion.title}
                  date={item.scheduledQuestion.updateDateUTC}
                  readed={item.scheduledQuestion.readed}
                  emailDelivery={emailFeatureOn ? item.emailDelivery ?? null : null}
                  canSendToOwner={!!ownerEmail}
                  onToggleEmail={(next) => handleToggleEmail(item, next)}
                  onManageEmail={() => setEmailModalItem(item)}
                  handleDelete={() => {
                    handleDelete(
                      item.scheduledQuestion.id,
                      item.scheduledQuestion.title
                    );
                  }}
                  handleRead={() => {
                    item.scheduledQuestion.readed = true;
                    setInstantReaded([...instantReaded, item.scheduledQuestion.id]);
                    handleRead(item);
                    selectInstance.state = item.scheduledQuestion.jobId;
                  }}
                />
              )
            )}
            {displayData.length > 10 &&
              listCoefficient < displayData.length && (
                <ListButton
                  text="Show more"
                  className={"ml-auto font-bold"}
                  onClick={() => setListCoefficient((prev) => prev + 8)}
                />
              )}

            {displayData.length &&
              listCoefficient >= displayData.length &&
              listCoefficient > 10 && (
                <ListButton
                  text="Show less"
                  className={"ml-auto font-bold"}
                  onClick={() => setListCoefficient(10)}
                />
              )}
          </>
        ) : status === "loading" ? (
          <div
            className="flex flex-col gap-2 mx-auto mt-[90px] items-center"
            role="status"
            aria-live="polite"
          >
            <LoaderCircle className="w-8 h-8 text-dark-blue-100 animate-spin" aria-hidden="true" />
            <p className="text-minimal text-dimgray-200 font-notosans">Loading your tasks…</p>
          </div>
        ) : status === "error" ? (
          <div className="flex flex-col items-center" role="alert">
            <Empty>Your tasks could not be loaded.</Empty>
            <ListButton text="Try again" className="font-bold" onClick={onRefreshData} />
          </div>
        ) : (
          <Empty>No Tasks Yet</Empty>
        )}
      </div>

      <DeleteModal
        title={deleteModalInfo?.title}
        isOpen={!!deleteModalInfo}
        onConfirm={confirmDelete}
        onCancel={handleCancel}
      />

      <ScheduledEmailSettingsModal
        open={!!emailModalItem}
        title={emailModalItem?.scheduledQuestion?.title}
        ownerEmail={ownerEmail}
        settings={emailModalItem?.emailDelivery}
        onSave={(settings) => handleSaveEmailSettings(emailModalItem, settings)}
        onClose={() => setEmailModalItem(null)}
      />

      <GrtAnalyticsGA4 onLoaded={handleAnalytics} />
    </div>
  );
};

export default Scheduled;